import { generatePkcePair } from './pkce';
import { jwtDecode } from 'jwt-decode';

/**
 * Azure AD OAuth2 Authentication Service for Single-Page Application (SPA)
 * 
 * This service implements OAuth2 Authorization Code Flow with PKCE (Proof Key for Code Exchange)
 * for Azure AD Single-Page Applications.
 * 
 * Key points:
 * - Uses PKCE flow (no client_secret required)
 * - All token requests are made from the browser (same origin)
 * - redirect_uri must match exactly between authorization and token requests
 * - Compatible with Azure AD SPA platform type
 */
class AuthService {
  constructor() {
    // Azure AD Configuration for Single-Page Application (SPA)
    this.tenantId = process.env.REACT_APP_AZURE_TENANT_ID;
    this.clientId = process.env.REACT_APP_AZURE_CLIENT_ID;
    // Note: client_secret is NOT used for SPAs - SPAs use PKCE flow only
    this.scope = process.env.REACT_APP_AZURE_SCOPE || 'openid profile email';
    
    // Azure AD OIDC Endpoints
    this.authority = `https://login.microsoftonline.com/${this.tenantId}`;
    this.authEndpoint = `${this.authority}/oauth2/v2.0/authorize`;
    this.tokenEndpoint = `${this.authority}/oauth2/v2.0/token`;
    this.logoutEndpoint = `${this.authority}/oauth2/v2.0/logout`;
    
    // Store PKCE code verifier for token exchange
    this.codeVerifier = null;
  }

  getAppUrl() {
    const location = window.location;
    
    // For SPAs, redirect URI must match exactly what's registered in Azure AD
    // Use the exact origin (protocol + host + port) without path
    // This ensures consistency between authorization and token requests
    let appUrl;
    
    if (location.hostname === 'localhost') {
      // For localhost, use explicit port (default 3000 for React)
      const port = location.port || '3000';
      appUrl = `${location.protocol}//${location.hostname}:${port}`;
    } else {
      // For production, use exact origin
      appUrl = `${location.protocol}//${location.host}`;
    }
    
    // Store for consistency across requests
    const storedUrl = localStorage.getItem('appUrl');
    if (storedUrl && storedUrl === appUrl) {
      // Use stored URL if it matches current origin (ensures consistency)
      return storedUrl;
    }
    
    localStorage.setItem('appUrl', appUrl);
    return appUrl;
  }

  async buildAuthorizationUrl() {
    // Get and store redirect_uri to ensure consistency
    const redirect_uri = this.getAppUrl();
    
    // Store redirect_uri for use in token exchange (must match exactly)
    localStorage.setItem('appUrl', redirect_uri);
    console.log('Authorization redirect_uri:', redirect_uri);
    
    // Generate PKCE pair
    const { codeVerifier, codeChallenge } = await generatePkcePair();
    this.codeVerifier = codeVerifier; // Store for token exchange
    
    // Store code verifier in sessionStorage for token exchange
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    
    const params = new URLSearchParams({
      client_id: this.clientId,
      response_type: 'code',
      redirect_uri: redirect_uri, // Store this exact value for token exchange
      response_mode: 'query',
      scope: this.scope,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state: this.generateState(), // CSRF protection
    });
    
    const authUrl = `${this.authEndpoint}?${params.toString()}`;
    console.log('Generated Azure AD Auth URL (redirect_uri will be used in token exchange)');
    
    return authUrl;
  }

  generateState() {
    // Generate a random state for CSRF protection
    const state = Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    sessionStorage.setItem('oauth_state', state);
    return state;
  }

  validateState(state) {
    const storedState = sessionStorage.getItem('oauth_state');
    return Boolean(storedState && storedState === state);
  }

  async exchangeCodeForTokens(code, state) {
    console.log('Exchanging authorization code for tokens with Azure AD');
    
    // Validate state for CSRF protection
    if (state && !this.validateState(state)) {
      throw new Error('Invalid state parameter - possible CSRF attack');
    }
    
    // Use the SAME redirect_uri that was used in the authorization request
    // This is critical to avoid cross-origin token redemption errors
    let redirect_uri = localStorage.getItem('appUrl') || this.getAppUrl();
    
    // Ensure redirect_uri matches the current origin exactly
    const currentOrigin = `${window.location.protocol}//${window.location.host}`;
    if (redirect_uri !== currentOrigin) {
      console.warn('Redirect URI mismatch, using current origin:', {
        stored: redirect_uri,
        current: currentOrigin
      });
      // Use current origin to ensure consistency
      redirect_uri = currentOrigin;
      localStorage.setItem('appUrl', redirect_uri);
    }
    
    console.log('Using redirect_uri:', redirect_uri);
    
    // Retrieve code verifier from sessionStorage
    const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
    if (!codeVerifier) {
      throw new Error('PKCE code verifier not found');
    }
    
    // For SPA, we must NOT include client_secret
    // Azure AD SPAs use PKCE only (no client secret)
    // The redirect_uri MUST match exactly what was used in the authorization request
    const body = new URLSearchParams({
      client_id: this.clientId,
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: redirect_uri, // CRITICAL: Must match authorization request exactly
      code_verifier: codeVerifier,
      scope: this.scope,
    });

    // DO NOT add client_secret for SPA - it will cause cross-origin token redemption error
    // SPAs must use PKCE flow only (no client secret)

    console.log('Token request details:', {
      client_id: this.clientId,
      grant_type: 'authorization_code',
      redirect_uri: redirect_uri,
      scope: this.scope,
      endpoint: this.tokenEndpoint
    });

    // Make the token request from the same origin
    // This is critical for SPAs - the request must come from the browser
    const response = await fetch(this.tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        // Do not set Origin or Referer headers - let browser set them automatically
      },
      body: body.toString(), // Convert URLSearchParams to string
      // Ensure credentials are not sent (SPA should not send cookies)
      credentials: 'omit',
      // Ensure the request is made from the current origin
      mode: 'cors', // Explicitly set CORS mode
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange failed:', response.status, errorText);
      throw new Error(`Failed to exchange code for tokens: ${response.status}`);
    }
    
    console.log('Token exchange response:', response.status);
    const tokens = await response.json();
    console.log('Tokens received from Azure AD');

    sessionStorage.removeItem('oauth_state');
    
    // Store tokens
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('id_token', tokens.id_token);
    if (tokens.refresh_token) {
      localStorage.setItem('refresh_token', tokens.refresh_token);
    }
    
    // Clear PKCE code verifier after successful exchange
    sessionStorage.removeItem('pkce_code_verifier');
    
    return this.getUserInfo();
  }

  parseJwt(token) {
    try {
      const base64Payload = token.split('.')[1];
      const payload = atob(base64Payload);
      return JSON.parse(payload);
    } catch (e) {
      return null;
    }
  }

  /**
   * Check if token is expired using JWT expiration claim
   * Azure AD doesn't provide introspection endpoint, so we rely on JWT expiration
   * @param {string} token - The access token to check
   * @returns {boolean} True if token is expired or invalid, false if valid
   */
  async isTokenExpired(token) {
    if (!token) return true;

    try {
      const decoded = this.parseJwt(token);
      if (!decoded || !decoded.exp) {
        return true; // Can't determine expiration, assume expired
      }

      const now = Math.floor(Date.now() / 1000);
      // Consider token expired if it expires within 60 seconds
      return decoded.exp < (now + 60);
    } catch (error) {
      console.error('Error checking token expiration:', error);
      // On error, assume token is expired for security
      return true;
    }
  }

  async refreshToken() {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      throw new Error('No refresh token available');
    }

    // Use the same redirect_uri that was used during initial authorization
    const redirect_uri = localStorage.getItem('appUrl') || this.getAppUrl();

    const body = new URLSearchParams({
      client_id: this.clientId,
      grant_type: 'refresh_token',
      refresh_token: refresh_token,
      redirect_uri: redirect_uri, // Must match the original redirect_uri
      scope: this.scope,
    });

    // DO NOT add client_secret for SPA - SPAs use PKCE flow only
    // Azure AD SPAs do not support client secrets in token requests

    const response = await fetch(this.tokenEndpoint, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(), // Convert to string
      credentials: 'omit', // SPA should not send cookies
      mode: 'cors', // Explicitly set CORS mode
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token refresh failed:', response.status, errorText);
      throw new Error('Token refresh failed');
    }

    const tokens = await response.json();
    return tokens;
  }

  async refreshIfNeeded() {
    const accessToken = localStorage.getItem('access_token');
    const refresh_token = localStorage.getItem('refresh_token');

    // No access token but we still have a refresh token — recover session silently
    if (!accessToken && refresh_token) {
      try {
        const refreshed = await this.refreshToken();
        if (this.applyRefreshedTokens(refreshed)) {
          return this.getUserInfo();
        }
      } catch (err) {
        console.error('Refresh token failed (no access token)', err);
        this.logoutLocal();
        throw err;
      }
      this.logoutLocal();
      return null;
    }

    if (!accessToken) {
      return null;
    }

    const isAccessTokenExpired = await this.isTokenExpired(accessToken);

    if (!isAccessTokenExpired) {
      return this.getUserInfo();
    }

    if (!refresh_token) {
      this.logoutLocal();
      return null;
    }

    try {
      const refreshed = await this.refreshToken();
      if (this.applyRefreshedTokens(refreshed)) {
        return this.getUserInfo();
      }
      throw new Error('Refresh failed - no access token in response');
    } catch (err) {
      console.error('Refresh token failed', err);
      this.logoutLocal();
      throw err;
    }
  }

  getUserInfo() {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) return null;
      
      const decoded = jwtDecode(idToken);
      if (!decoded) return null;

      // Azure AD JWT token claims
      // Common claims: name, preferred_username, email, upn, given_name, family_name, sub, oid
      const userName = decoded.name || 
                       decoded.preferred_username || 
                       decoded.given_name || 
                       decoded.sub || 
                       '';
      
      const email = decoded.email || 
                    decoded.preferred_username || 
                    decoded.upn || 
                    '';
      
      // Use email as userId if available, otherwise use oid (object ID) or sub
      const userId = email || 
                     decoded.oid || 
                     decoded.sub || 
                     userName || 
                     '';

      if (!userName && !userId) {
        console.warn('Could not extract user info from Azure AD token:', decoded);
        return null;
      }

      return {
        userName: userName,
        userId: userId,
        email: email,
        givenName: decoded.given_name || '',
        familyName: decoded.family_name || '',
        oid: decoded.oid || decoded.sub || '',
      };
    } catch (error) {
      console.error('Error decoding user info from Azure AD token:', error);
      return null;
    }
  }

  /**
   * Build Azure AD logout URL
   * @returns {string} Logout URL
   */
  buildLogoutUrl() {
    const redirect_uri = this.getAppUrl();
    const params = new URLSearchParams({
      post_logout_redirect_uri: redirect_uri,
    });
    
    return `${this.logoutEndpoint}?${params.toString()}`;
  }

  /**
   * Clear local session only (no Azure redirect). Use when refresh fails or session is invalid.
   */
  logoutLocal() {
    try {
      localStorage.clear();
    } catch (e) {
      console.warn("localStorage.clear failed:", e);
    }

    sessionStorage.removeItem('pkce_code_verifier');
    sessionStorage.removeItem('oauth_state');
  }

  /**
   * Logout from Azure AD
   * Clears local storage and redirects to Azure AD logout endpoint
   */
  logout() {
    this.logoutLocal();

    const logoutUrl = this.buildLogoutUrl();
    console.log('Redirecting to Azure AD logout:', logoutUrl);
    window.location.href = logoutUrl;
  }

  /**
   * Apply token payload from refresh_token grant. id_token may be omitted by Azure; keep existing id_token when absent.
   */
  applyRefreshedTokens(tokens) {
    if (!tokens?.access_token) {
      return false;
    }
    localStorage.setItem('access_token', tokens.access_token);
    if (tokens.id_token) {
      localStorage.setItem('id_token', tokens.id_token);
    }
    if (tokens.refresh_token) {
      localStorage.setItem('refresh_token', tokens.refresh_token);
    }
    return true;
  }
}

export default AuthService;
